import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import { FC, ForwardedRef, forwardRef } from 'react';
import Loading from '../Loading/Loading';
import { LoadingVariants } from '../Loading/Loading.types';
import { PrimaryButton, PrimaryWordWrapButton, SecondaryButton, TertiaryButton, TextButton } from './Button.styles';
import { ButtonProps, CaretPosition, Variant } from './Button.types';

const Button: FC<ButtonProps> = forwardRef(
  (
    { label, variant, isSmall = false, caret, isLoading = false, loadingLabel = 'Saving...', testId, ...props },
    ref: ForwardedRef<HTMLButtonElement>
  ) => {
    switch (variant) {
      case Variant.Primary:
        return (
          <PrimaryButton
            {...props}
            ref={ref}
            isSmall={isSmall}
            isLoading={isLoading}
            data-testid={ testId ?? 'PrimaryButton' }
            disabled={props.disabled || isLoading}
          >
            {isLoading ? loadingLabel : label}
            {isLoading ? <Loading size={8} variant={LoadingVariants.Secondary} /> : <></>}
          </PrimaryButton>
        );
      case Variant.Secondary:
        return (
          <SecondaryButton
            {...props}
            ref={ref}
            isSmall={isSmall}
            isLoading={isLoading}
            data-testid={ testId ?? 'SecondaryButton' }
            disabled={props.disabled || isLoading}
          >
            {isLoading ? loadingLabel : label}
            {isLoading ? <Loading size={8} variant={LoadingVariants.Primary} /> : <></>}
          </SecondaryButton>
        );
      case Variant.Tertiary:
        return (
          <TertiaryButton
            {...props}
            ref={ref}
            isSmall={isSmall}
            isLoading={isLoading}
            data-testid={ testId ?? 'TertiaryButton' }
            disabled={props.disabled || isLoading}
          >
            {isLoading ? loadingLabel : label}
            {isLoading ? <Loading size={8} variant={LoadingVariants.Primary} /> : <></>}
          </TertiaryButton>
        );
      case Variant.Text:
        return (
          <TextButton
            {...props}
            caret={caret}
            ref={ref}
            data-testid={ testId ?? 'TextButton' }
            disabled={props.disabled || isLoading}
            isLoading={isLoading}
          >
            {caret === CaretPosition.Left && <ChevronLeftOutlinedIcon />}
            {isLoading ? loadingLabel : label}
            {isLoading ? <Loading size={8} variant={LoadingVariants.Primary} /> : <></>}
            {caret === CaretPosition.Right && <ChevronRightOutlinedIcon />}
          </TextButton>
        );
      case Variant.PrimaryWordWrap:
        return (
          <PrimaryWordWrapButton
            {...props}
            caret={caret}
            ref={ref}
            data-testid={ testId ?? 'TextButton' }
            disabled={props.disabled || isLoading}
            isLoading={isLoading}
          >
            {caret === CaretPosition.Left && <ChevronLeftOutlinedIcon />}
            {isLoading ? loadingLabel : label}
            {isLoading ? <Loading size={8} variant={LoadingVariants.Primary} /> : <></>}
            {caret === CaretPosition.Right && <ChevronRightOutlinedIcon />}
          </PrimaryWordWrapButton>
        );
      default:
        return (
          <PrimaryButton
            {...props}
            ref={ref}
            isSmall={isSmall}
            isLoading={isLoading}
            data-testid={ testId ?? 'PrimaryButton' }
            disabled={props.disabled || isLoading}
          >
            {isLoading ? loadingLabel : label}
            {isLoading ? <Loading size={8} variant={LoadingVariants.Secondary} /> : <></>}
          </PrimaryButton>
        );
    }
  }
);

export default Button;
